import { Injectable } from '@angular/core'
import { LoggerService } from '@core/services/infra/logger.service';;
import { BehaviorSubject } from 'rxjs';
import { ContextAwareNavigationService } from '@core/services/navigation/context-aware-navigation.service';

export interface ContextualNavigationItem {
  id: string;
  label: string;
  icon: string;
  route?: string;
  badge?: string;
  /**
   * Classes de la pastille (fond + texte). Sans elle, la pastille garde son
   * apparence neutre — un décompte n'a pas à se colorier, un pourcentage de
   * complétude si.
   */
  badgeClass?: string;
  disabled?: boolean;
  active?: boolean;
  /**
   * Routes secondaires qui allument aussi l'entrée — une entrée qui regroupe
   * plusieurs pages (« Charges et dépenses » : Dépenses et Décomptes) reste
   * surlignée sur chacune. Même frontière de segment que `routerLinkActive`.
   */
  alsoActiveOn?: string[];
  clickHandler?: () => void;
  children?: ContextualNavigationItem[];
}

export interface EntityContext {
  type: 'property' | 'contract' | 'tenant' | 'loan' | 'building' | 'assembly' | 'repair' | 'maintenance' | 'investment-calculator' | 'admin' | 'help' | 'account-settings' | 'communication' | 'service-provider' | 'workspace';
  id: string;
  name: string;
  /**
   * Subtitle: a translation KEY (translated by the sidebar at render time) or a
   * plain dynamic string (rendered as-is when it matches no key). When the key
   * needs interpolation params (e.g. a `{{count}}`), pass them via
   * `subtitleParams` — DO NOT pre-translate here and store the result, because
   * the sidebar pipes the subtitle through `translate` again, which would lose
   * the params and leak the raw `{{count}}` placeholder.
   */
  subtitle?: string;
  /** Interpolation params for `subtitle` when it is a translation key. */
  subtitleParams?: Record<string, unknown>;
  sourceRoute?: string; // Track where we came from
}

@Injectable({
  providedIn: 'root'
})
export class ContextualSidebarService {
  private readonly CONTEXT = 'ContextualSidebarService';

  private _isVisible$ = new BehaviorSubject<boolean>(false);
  private _context$ = new BehaviorSubject<EntityContext | null>(null);
  private _navigationItems$ = new BehaviorSubject<ContextualNavigationItem[]>([]);

  // Public observables
  isVisible$ = this._isVisible$.asObservable();
  context$ = this._context$.asObservable();
  navigationItems$ = this._navigationItems$.asObservable();

  constructor(private contextAwareNavigation: ContextAwareNavigationService, private logger: LoggerService) { }

  showSidebar(context: EntityContext, items: ContextualNavigationItem[]) {
    // Capture the current navigation context to know where we came from
    const currentNavContext = this.contextAwareNavigation.getCurrentContext();

    // If we're showing a sidebar for a different entity type than the current context,
    // this is cross-entity navigation - preserve the original context
    if (currentNavContext &&
      currentNavContext.entityType &&
      currentNavContext.entityType !== context.type &&
      currentNavContext.entityId) {

      // Set cross-entity context to preserve navigation chain
      this.contextAwareNavigation.setCrossEntityContext(
        context.type,
        context.id,
        currentNavContext
      );
    }

    // Set sourceRoute if not already set
    if (currentNavContext && !context.sourceRoute) {
      context.sourceRoute = currentNavContext.sourceRoute;
    }

    this._context$.next(context);
    this._navigationItems$.next(items);
    this._isVisible$.next(true);
  }

  /**
   * Reset navigation and clear history
   * Used for the "Hard Reset" functionality
   */
  reset() {
    this.hideSidebar();
    this.contextAwareNavigation.clearHistory();
  }

  hideSidebar() {
    this._isVisible$.next(false);
    this._context$.next(null);
    this._navigationItems$.next([]);
  }

  updateContext(context: EntityContext) {
    this._context$.next(context);
  }

  updateNavigationItems(items: ContextualNavigationItem[]) {
    this._navigationItems$.next(items);
  }

  getCurrentNavigationItems(): ContextualNavigationItem[] {
    return this._navigationItems$.value;
  }

  getCurrentContext(): EntityContext | null {
    return this._context$.value;
  }

  /**
   * Update a specific navigation item's badge by its ID
   * Useful for real-time updates like listing quality scores
   */
  updateNavigationItemBadge(itemId: string, newBadge: string): void {
    const currentItems = this._navigationItems$.value;
    const updatedItems = this.updateBadgeRecursive(currentItems, itemId, newBadge);
    this._navigationItems$.next(updatedItems);
  }

  /**
   * Recursively update badge in navigation items (handles nested children)
   */
  private updateBadgeRecursive(items: ContextualNavigationItem[], itemId: string, newBadge: string): ContextualNavigationItem[] {
    return items.map(item => {
      if (item.id === itemId) {
        return { ...item, badge: newBadge };
      }
      if (item.children) {
        return { ...item, children: this.updateBadgeRecursive(item.children, itemId, newBadge) };
      }
      return item;
    });
  }

  /**
   * Get the appropriate back route based on the current context
   */
  getBackRoute(): string {
    const context = this.getCurrentContext();
    if (!context) return '/';

    // First, try to use the context-aware navigation service
    const navContext = this.contextAwareNavigation.getCurrentContext();
    if (navContext) {
      // If we have navigation history, try to get the appropriate return route
      const returnRoute = this.contextAwareNavigation.getReturnRoute();
      if (returnRoute && returnRoute !== '/financial-dashboard' && returnRoute !== '/dashboard') {
        return returnRoute;
      }
    }

    // Fallback to entity-specific list routes
    switch (context.type) {
      case 'property': return '/properties/list';
      case 'contract': return '/contracts/list';
      case 'tenant': return '/tenants/list';
      case 'loan': return '/loans/list';
      case 'building': return '/properties/list';
      case 'assembly': return '/syndic/dashboard';
      case 'repair': return '/repairs/list';
      case 'maintenance': return '/maintenance/list';
      case 'investment-calculator': return '/property-evaluation/investment-calculator/list';
      case 'admin': return '/admin';
      case 'help': return '/help/user-guide';
      case 'account-settings': return '/apps/user-profile';
      case 'communication': return '/apps/inbox';
      default: return '/financial-dashboard';
    }
  }

  /**
   * Navigate back using context-aware navigation
   */
  navigateBack(): Promise<boolean> {
    // Use the context-aware navigation service's navigateBack method
    return this.contextAwareNavigation.navigateBack();
  }

  /**
   * Show Global Admin contextual sidebar
   *
   * Labels here are translation keys, not raw strings — the sidenav
   * template pipes `item.label` through `| translate` so the active
   * language wins. Keys live under `shared.contextual.admin.*` in the
   * i18n bundle (see `src/assets/i18n/{en,fr,nl,de}.json`).
   */
  showAdminSidebar() {
    const adminContext: EntityContext = {
      type: 'admin',
      id: 'global-admin',
      name: 'shared.contextual.admin.title',
      subtitle: 'shared.contextual.admin.subtitle'
    };

    const adminNavigation: ContextualNavigationItem[] = [
      {
        id: 'dashboard',
        label: 'shared.contextual.admin.dashboard',
        icon: 'dashboard',
        route: '/admin/dashboard'
      },
      {
        id: 'marketing',
        label: 'shared.contextual.admin.marketing',
        icon: 'campaign',
        children: [
          {
            id: 'landing-pages',
            label: 'shared.contextual.admin.landingPages',
            icon: 'ad_units',
            route: '/admin/landing-pages'
          },
          {
            id: 'blog',
            label: 'shared.contextual.admin.blog',
            icon: 'article',
            route: '/admin/blog'
          },
          {
            id: 'marketing-sequences',
            label: 'shared.contextual.admin.marketingSequences',
            icon: 'mark_email_read',
            route: '/admin/marketing/sequences'
          },
          {
            id: 'marketing-leads',
            label: 'shared.contextual.admin.marketingLeads',
            icon: 'contact_mail',
            route: '/admin/marketing/leads'
          },
          {
            id: 'indexation-leads',
            label: 'shared.contextual.admin.indexationLeads',
            icon: 'calculate',
            route: '/admin/marketing/indexation'
          },
          {
            id: 'engagement',
            label: 'shared.contextual.admin.engagement',
            icon: 'insights',
            route: '/admin/engagement'
          },
          {
            id: 'lifecycle-emails',
            label: 'shared.contextual.admin.lifecycleEmails',
            icon: 'forward_to_inbox',
            route: '/admin/lifecycle-emails'
          }
        ]
      },
      {
        id: 'commercial',
        label: 'shared.contextual.admin.commercial',
        icon: 'leaderboard',
        children: [
          {
            id: 'signups',
            label: 'shared.contextual.admin.signups',
            icon: 'person_add',
            route: '/admin/signups'
          },
          {
            id: 'leads',
            label: 'shared.contextual.admin.leads',
            icon: 'view_kanban',
            route: '/admin/leads'
          },
          {
            id: 'beta-requests',
            label: 'shared.contextual.admin.betaRequests',
            icon: 'science',
            route: '/admin/beta-requests'
          },
          {
            id: 'revenue',
            label: 'shared.contextual.admin.revenue',
            icon: 'paid',
            route: '/admin/revenue'
          },
          {
            id: 'funnel',
            label: 'shared.contextual.admin.funnel',
            icon: 'filter_alt',
            route: '/admin/funnel'
          },
          {
            id: 'cohorts',
            label: 'shared.contextual.admin.cohorts',
            icon: 'grid_on',
            route: '/admin/cohorts'
          }
        ]
      },
      {
        id: 'users',
        label: 'shared.contextual.admin.users',
        icon: 'people_outline',
        route: '/admin/users'
      },
      {
        id: 'workspaces',
        label: 'shared.contextual.admin.workspaces',
        icon: 'business',
        route: '/admin/workspaces'
      },
      {
        id: 'ops',
        label: 'shared.contextual.admin.healthAudit',
        icon: 'monitor_heart',
        children: [
          {
            id: 'health',
            label: 'shared.contextual.admin.workspaceHealth',
            icon: 'health_and_safety',
            route: '/admin/health'
          },
          {
            id: 'audit',
            label: 'shared.contextual.admin.auditLog',
            icon: 'history',
            route: '/admin/audit'
          },
          {
            id: 'projections',
            label: 'shared.contextual.admin.projectionEngine',
            icon: 'hub',
            route: '/admin/projections'
          },
          {
            id: 'bank-connections',
            label: 'shared.contextual.admin.bankConnections',
            icon: 'account_balance',
            route: '/admin/bank-connections'
          }
        ]
      },
      {
        id: 'configuration',
        label: 'shared.contextual.admin.configuration',
        icon: 'settings',
        route: '/admin/configuration',
        children: [
          {
            id: 'config-templates',
            label: 'shared.contextual.admin.configTemplates',
            icon: 'email',
            route: '/admin/configuration/templates'
          },
          {
            id: 'config-contract-templates',
            label: 'shared.contextual.admin.configContractTemplates',
            icon: 'gavel',
            route: '/admin/configuration/contract-templates'
          }
        ]
      },
      {
        id: 'data-management',
        label: 'shared.contextual.admin.dataManagement',
        icon: 'storage',
        children: [
          {
            id: 'variables',
            label: 'shared.contextual.admin.dataVariables',
            icon: 'tune',
            route: '/admin/variables'
          },
          {
            id: 'public-providers',
            label: 'shared.contextual.admin.publicProviders',
            icon: 'engineering',
            route: '/admin/public-providers'
          },
          {
            id: 'indexation',
            label: 'shared.contextual.admin.dataIndexation',
            icon: 'trending_up',
            route: '/admin/indexation'
          },
          {
            id: 'test-data',
            label: 'shared.contextual.admin.testData',
            icon: 'science',
            route: '/admin/test-data'
          },
          {
            id: 'phone-search',
            label: 'shared.contextual.admin.phoneSearch',
            icon: 'phone_forwarded',
            route: '/admin/phone-search'
          }
        ]
      },
      {
        id: 'analytics',
        label: 'shared.contextual.admin.analytics',
        icon: 'analytics',
        children: [
          {
            id: 'analytics-dashboard',
            label: 'shared.contextual.admin.analyticsDashboard',
            icon: 'insights',
            route: '/admin/analytics'
          },
          {
            id: 'sent-items',
            label: 'shared.contextual.admin.sentItems',
            icon: 'send',
            route: '/admin/sent-items'
          },
          {
            id: 'email-health',
            label: 'shared.contextual.admin.emailHealth',
            icon: 'mark_email_unread',
            route: '/admin/email-health'
          },
          {
            id: 'usage',
            label: 'shared.contextual.admin.usage',
            icon: 'bar_chart',
            route: '/admin/usage'
          }
        ]
      },
      {
        id: 'ai-cockpit',
        label: 'shared.contextual.admin.aiCockpit',
        icon: 'auto_awesome',
        children: [
          {
            id: 'ai-configuration',
            label: 'shared.contextual.admin.aiConfiguration',
            icon: 'psychology',
            route: '/admin/ai-configuration'
          },
          {
            id: 'ai-usage',
            label: 'shared.contextual.admin.aiUsage',
            icon: 'query_stats',
            route: '/admin/ai-usage'
          },
          {
            id: 'ai-feedback',
            label: 'shared.contextual.admin.aiFeedback',
            icon: 'thumb_up',
            route: '/admin/ai-feedback'
          },
          {
            id: 'widget-gallery',
            label: 'shared.contextual.admin.aiWidgetGallery',
            icon: 'widgets',
            route: '/admin/widget-gallery'
          },
          {
            id: 'cockpit-intents',
            label: 'shared.contextual.admin.cockpitIntents',
            icon: 'route',
            route: '/admin/cockpit-intents'
          },
          {
            id: 'cockpit-errors',
            label: 'shared.contextual.admin.cockpitErrors',
            icon: 'error_outline',
            route: '/admin/cockpit-errors'
          }
        ]
      },
      {
        id: 'whatsapp-group',
        label: 'shared.contextual.admin.whatsappGroup',
        icon: 'chat',
        children: [
          {
            id: 'whatsapp-test',
            label: 'shared.contextual.admin.whatsappTesting',
            icon: 'chat',
            route: '/admin/whatsapp-test'
          },
          {
            id: 'whatsapp-conversations',
            label: 'shared.contextual.admin.whatsappConversations',
            icon: 'forum',
            route: '/admin/whatsapp-conversations'
          },
          {
            id: 'whatsapp-unassigned',
            label: 'shared.contextual.admin.whatsappUnassigned',
            icon: 'move_to_inbox',
            route: '/admin/whatsapp-unassigned'
          },
          {
            id: 'whatsapp-reset',
            label: 'shared.contextual.admin.whatsappReset',
            icon: 'restart_alt',
            route: '/admin/whatsapp-reset'
          },
          {
            id: 'whatsapp-diagnose',
            label: 'shared.contextual.admin.whatsappDiagnose',
            icon: 'troubleshoot',
            route: '/admin/whatsapp-diagnose'
          }
        ]
      },
      {
        id: 'system-tools',
        label: 'shared.contextual.admin.systemTools',
        icon: 'build',
        children: [
          {
            id: 'email-aliases',
            label: 'shared.contextual.admin.emailAliases',
            icon: 'alternate_email',
            route: '/admin/email-aliases'
          },
          {
            id: 'email-test',
            label: 'shared.contextual.admin.emailTesting',
            icon: 'email',
            route: '/admin/email-test'
          },
          {
            id: 'logs',
            label: 'shared.contextual.admin.systemLogs',
            icon: 'description',
            route: '/admin/logs'
          },
          {
            id: 'system-health',
            label: 'shared.contextual.admin.systemHealth',
            icon: 'monitor_heart',
            route: '/admin/system-health'
          },
          {
            id: 'support',
            label: 'shared.contextual.admin.support',
            icon: 'support_agent',
            route: '/admin/support'
          }
        ]
      }
    ];

    this.showSidebar(adminContext, adminNavigation);
  }

}